import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { AssessmentDocumentStatus, AssessmentDomainStatus, FileStatus } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import { AssessmentDomainDocument, AssessmentDomainDocumentDetail, FileUploadType } from '@libs/types';

import { useDeleteDocument } from '../api';
import { AssessmentDocumentViewModel } from '../types';
import { mapToDocumentFilesViewModel } from '../utils/assessment-mapper';
import { getLocalizedErrorMessage } from '../utils/error-utils';

import { useAssessmentDocumentUpload } from './useAssessmentDocumentUpload';

type FileEntry = { file: File; uploadId: string; uploadedAt: string };

/**
 * Custom hook for managing file operations in assessment workflow
 * - Upload files
 * - Delete files
 * - Cancel uploads
 * - Track upload state
 */
export const useAssessmentFileOperations = (
  assessmentId: string,
  maxRequiredDocuments: number,
  minRequiredDocuments: number,
  assessmentDomainId: string,
  domainDocuments?: AssessmentDomainDocument[],
  domainStatus?: string,
): {
  filesArray: AssessmentDocumentViewModel[];
  deletedFiles: Set<string>;
  handleAddFiles: (newFiles: File[]) => Promise<void>;
  handleDeleteFile: (fileId: string) => Promise<void>;
  handleUndoDelete: (fileId: string) => void;
  handleRetryDelete: (fileId: string) => Promise<void>;
  handleRetryUpload: (fileId: string) => Promise<void>;
  handleRemoveFailedUpload: (fileId: string) => void;
  handleCancelUpload: (uploadId: string) => void;
  markFileAsMoved: (documentId: string) => void;
  clearDeletedFiles: () => void;
  restoreDeletedFiles: () => void;
} => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { t } = useTrans();
  // ============================================================================
  // STATE
  // ============================================================================

  /** Tracks upload progress and errors for each file */
  // const [uploadStates, setUploadStates] = useState<Map<string, UploadState>>(new Map());

  /** Optimistic UI: Files currently being uploaded (queued, uploading, failed) */
  const [uploadStates, setUploadStates] = useState<Map<string, FileUploadType>>(new Map());

  /** Tracks files marked for deletion (pending save) */
  const [deletedFiles, setDeletedFiles] = useState<Set<string>>(new Set());

  /**
   * Documents moved to another domain. Hidden from this domain's list
   * immediately on a successful move so the item doesn't linger until the
   * background refetch lands. Cleared when the domain changes.
   */
  const [movedFiles, setMovedFiles] = useState<Set<string>>(new Set());

  /** Stores original file data before deletion for restoration */
  const [deletedFilesData, setDeletedFilesData] = useState<Map<string, FileUploadType>>(new Map());

  /** Stores timeout IDs for pending deletions */
  const deleteTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  /** Stores interval IDs for countdown timers */
  const countdownIntervalsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  /** Stores original File objects for retry on failed uploads */
  const originalFilesRef = useRef<Map<string, File>>(new Map());

  /** Persistent queue of file entries waiting to be uploaded */
  const uploadQueueRef = useRef<FileEntry[]>([]);

  /** Whether the queue drain loop is currently running */
  const isProcessingRef = useRef(false);

  // ============================================================================
  // RESET STATE WHEN DOMAIN CHANGES
  // ============================================================================

  // Reset all file-related state when navigating to a different domain
  useEffect(() => {
    // Clear all upload states
    setUploadStates(new Map());
    setDeletedFiles(new Set());
    setDeletedFilesData(new Map());
    setMovedFiles(new Set());

    // Clear all timers
    deleteTimersRef.current.forEach((timer) => clearTimeout(timer));
    deleteTimersRef.current.clear();

    countdownIntervalsRef.current.forEach((interval) => clearInterval(interval));
    countdownIntervalsRef.current.clear();

    originalFilesRef.current.clear();
    uploadQueueRef.current = [];
    isProcessingRef.current = false;
  }, [assessmentDomainId]); // Reset when domain ID changes


  const filesArray = useMemo(() => {
    const apiFiles = mapToDocumentFilesViewModel(domainDocuments);
    const uploadingFilesArray = Array.from(uploadStates.values());

    // Remove duplicates: uploading files take precedence over API files
    const uploadingFileIds = new Set(uploadingFilesArray.map((f) => f.id));
    const filteredApiFiles = apiFiles.filter((f) => !uploadingFileIds.has(f.id));

    const updatedFilesArray: AssessmentDocumentViewModel[] = uploadingFilesArray.map((uploadFile) => {
      const apiMatch = apiFiles.find((apiFile) => apiFile.id === uploadFile.id);
      return apiMatch ? { ...apiMatch, ...uploadFile } : uploadFile;
    });

    return [...updatedFilesArray, ...filteredApiFiles]
      .sort((a, b) => new Date(a.uploadedAt || '').getTime() - new Date(b.uploadedAt || '').getTime())
      .filter(
        (file) =>
          !movedFiles.has(file.id) &&
          !(file.status === FileStatus.DELETED && file.documentStatus !== AssessmentDocumentStatus.CONFIRMED),
      );
  }, [domainDocuments, uploadStates, movedFiles, assessmentId]);


  // ============================================================================
  // API HOOKS
  // ============================================================================

  const { uploadDocument, cancelUpload } = useAssessmentDocumentUpload({
    assessmentId,
    assessmentDomainId,
  });

  const deleteDocumentMutation = useDeleteDocument();

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  // ============================================================================
  // FILE OPERATIONS
  // ============================================================================

  /**
   * Drain loop — processes the upload queue one file at a time.
   * Re-entrant safe: only one instance runs at a time, so files added
   * by separate one-by-one selections are serialized correctly.
   */
  const drainUploadQueue = useCallback(async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    while (uploadQueueRef.current.length > 0) {
      const { file, uploadId } = uploadQueueRef.current[0];

      // Entry was cancelled while waiting in queue — skip it
      if (!originalFilesRef.current.has(uploadId)) {
        uploadQueueRef.current.shift();
        continue;
      }

      try {
        await uploadDocument(file, {
          onStart: () => {
            setUploadStates((prev) => {
              const updated = new Map(prev);
              const existing = updated.get(uploadId);
              if (existing) {
                updated.set(uploadId, { ...existing, status: FileStatus.UPLOADING, progress: 0 });
              }
              return updated;
            });
          },
          onProgress: (progress) => {
            setUploadStates((prev) => {
              const updated = new Map(prev);
              const existing = updated.get(uploadId);
              if (existing) {
                updated.set(uploadId, { ...existing, progress });
              }
              return updated;
            });
          },
          onSuccess: (document: AssessmentDomainDocumentDetail) => {
            const confirmedId = document.documentId ?? uploadId;
            setUploadStates((prev) => {
              const updated = new Map(prev);
              const existing = updated.get(uploadId);
              if (existing) {
                updated.delete(uploadId);
                updated.set(confirmedId, {
                  ...existing,
                  id: confirmedId,
                  status: FileStatus.NOT_SAVED,
                  progress: 100,
                });
              }
              return updated;
            });
            originalFilesRef.current.delete(uploadId);
          },
          onError: (message) => {
            setUploadStates((prev) => {
              const updated = new Map(prev);
              const existing = updated.get(uploadId);
              if (existing) {
                updated.set(uploadId, {
                  ...existing,
                  status: FileStatus.FAILED,
                  progress: 0,
                  error: message,
                });
              }
              return updated;
            });
          },
        });
      } catch (error) {
        // AbortError: remove the entry silently
        if ((error as Error).name === 'AbortError') {
          setUploadStates((prev) => {
            const updated = new Map(prev);
            updated.delete(uploadId);
            return updated;
          });
          originalFilesRef.current.delete(uploadId);
        }
        // Other errors are already handled by the onError callback above
      }

      uploadQueueRef.current.shift();
    }

    isProcessingRef.current = false;
  }, [uploadDocument]);

  /**
   * Enqueues file entries and triggers the drain loop.
   * Sets all entries to QUEUED immediately for instant UI feedback,
   * then the drain loop processes them one at a time — even when
   * files arrive from separate one-by-one selections.
   */
  const uploadFileEntries = useCallback(
    async (fileEntries: FileEntry[]) => {
      // Show all incoming files as QUEUED immediately
      setUploadStates((prev) => {
        const updated = new Map(prev);
        for (const { file, uploadId, uploadedAt } of fileEntries) {
          updated.set(uploadId, {
            id: uploadId,
            fileName: file.name,
            size: file.size,
            status: FileStatus.QUEUED,
            progress: 0,
            uploadedAt,
          });
        }
        return updated;
      });

      for (const entry of fileEntries) {
        originalFilesRef.current.set(entry.uploadId, entry.file);
        uploadQueueRef.current.push(entry);
      }

      // Start draining — no-op if already running
      drainUploadQueue();
    },
    [drainUploadQueue],
  );

  /**
   * Handles new file uploads
   * - Validates domain status and file count limit
   * - Generates new IDs and timestamps for each file
   * - Delegates the actual upload to uploadFileEntries
   */
  const handleAddFiles = useCallback(
    async (newFiles: File[]) => {
      if (!assessmentId || !assessmentDomainId) return;

      // Validate domain status - must be IN_PROGRESS to upload
      if (domainStatus && domainStatus !== AssessmentDomainStatus.IN_PROGRESS) {
        enqueueSnackbar(t('app.assessment.notStarted'), {
          variant: 'warning',
        });
        return;
      }

      // Fast-fail pre-check: drop a file that's already in this domain before
      // it ever reaches S3, instead of letting the user wait through an
      // upload the API will reject at confirm time anyway. Same identity as
      // the API guard — fileName + size, case/whitespace-normalized, not a
      // content hash (see ConfirmUploadService for why there's no hash to
      // use). This is advisory only: `filesArray` can be stale, so a false
      // negative here just falls through to the real (API) rejection; it
      // can never let a true duplicate slip past the API.
      //
      // "Live" excludes DELETED/FAILED (as elsewhere in this hook) and also
      // PENDING_DELETE/DELETING: those are mid-removal but not actually
      // gone from the domain yet on the server, so the API would (rightly)
      // still reject a same-name reupload attempted at this exact instant —
      // but blocking it here too would punish the extremely common
      // "delete, then immediately drop the replacement in" flow for the
      // whole 5-second undo window. Letting it through here just means the
      // decision falls through to the API, which is authoritative anyway.
      const normalizeFileKey = (name: string, size: number) => `${name.trim().toLowerCase()}::${size}`;
      const liveFiles = filesArray.filter(
        (file) =>
          file.status !== FileStatus.DELETED &&
          file.status !== FileStatus.FAILED &&
          file.status !== FileStatus.PENDING_DELETE &&
          file.status !== FileStatus.DELETING,
      );
      const seenFileKeys = new Set(liveFiles.map((file) => normalizeFileKey(file.fileName, file.size)));
      const uniqueFiles: File[] = [];
      const skippedFileNames: string[] = [];

      for (const file of newFiles) {
        const key = normalizeFileKey(file.name, file.size);
        // Also catches the same file selected twice within this one batch —
        // neither copy is in `filesArray` yet, so only a running set here
        // can tell they collide with each other.
        if (seenFileKeys.has(key)) {
          skippedFileNames.push(file.name);
          continue;
        }
        seenFileKeys.add(key);
        uniqueFiles.push(file);
      }

      if (skippedFileNames.length > 0) {
        enqueueSnackbar(
          t('app.assessment.workflow.duplicateFileSkipped', {
            fileNames: skippedFileNames.join(', '),
          }),
          { variant: 'warning' },
        );
      }

      if (uniqueFiles.length === 0) return;

      const existingFilesCount = filesArray.filter(file => file.status !== FileStatus.DELETED && file.status !== FileStatus.FAILED).length;

      const totalAfterUpload = existingFilesCount + uniqueFiles.length;

      if (totalAfterUpload > maxRequiredDocuments) {
        const remaining = maxRequiredDocuments - existingFilesCount;
        enqueueSnackbar(
          remaining > 0
            ? t('app.assessment.workflow.maxFilesAllowed', { maxRequiredDocuments, remaining })
            : t('app.assessment.workflow.maxFilesAllowed', { maxRequiredDocuments }),
          { variant: 'warning' }
        );
        return;
      }

      // Generate IDs for all files upfront
      // Ensure each file gets a slightly incremented uploadedAt timestamp (by milliseconds)
      const now = Date.now();
      const fileEntries: FileEntry[] = uniqueFiles.map((file, i) => ({
        file,
        uploadId: crypto.randomUUID(),
        uploadedAt: new Date(now + i).toISOString(),
      }));

      await uploadFileEntries(fileEntries);
    },
    [assessmentId, assessmentDomainId, uploadFileEntries, enqueueSnackbar, domainStatus, maxRequiredDocuments, filesArray, t],
  );

  /**
   * Handles file deletion
   * - For uploading/queued/notSaved files: removes from local state only
   * - For uploaded files: marks as "deleted" (pending save to confirm deletion)
   * - Actual deletion happens on domain save
   * - Validates domain is IN_PROGRESS before allowing deletion
   */
  const handleDeleteFile = useCallback(
    async (fileId: string) => {
      // Validate domain status - must be IN_PROGRESS to delete
      if (domainStatus && domainStatus !== AssessmentDomainStatus.IN_PROGRESS) {
        enqueueSnackbar(t('app.assessment.workflow.notStarted'), {
          variant: 'warning',
        });
        return;
      }

      // Check if it's a failed/queued/notSaved upload (remove from local state only)
      const uploadingFile = uploadStates.get(fileId);

      // For uploaded files, start 5-second countdown before calling backend delete
      let fileToDelete: FileUploadType;

      if (uploadingFile) {
        fileToDelete = {
          ...uploadingFile
        }
      } else {
        const apiDomainDocument = domainDocuments?.find((doc) => doc.documentId === fileId);
        if (!apiDomainDocument) {
          enqueueSnackbar(t('app.assessment.workflow.noApiDocumentFound'), {
            variant: 'warning',
          });
          return
        }
        fileToDelete = {
          id: fileId,
          fileName: apiDomainDocument.fileName,
          size: apiDomainDocument.fileSize,
          status: FileStatus.UPLOADED,
          progress: 100,
          uploadedAt: apiDomainDocument.uploadedAt,
        }
      }


      // Store original file data for potential restoration

      setDeletedFilesData((prev) => new Map(prev).set(fileId, fileToDelete));

      // Mark file as "pending delete" with 5-second timer
      setUploadStates((prev) => {
        const updated = new Map(prev);
        updated.set(fileId, {
          ...fileToDelete,
          status: FileStatus.PENDING_DELETE,
          deleteTimer: 5, // Start countdown from 5 seconds
        });
        return updated;
      });

      // Start countdown timer (updates every second)
      const countdownInterval = setInterval(() => {
        setUploadStates((prev) => {
          const updated = new Map(prev);
          const file = updated.get(fileId);
          if (file && file.deleteTimer && file.deleteTimer > 0) {
            updated.set(fileId, {
              ...file,
              deleteTimer: file.deleteTimer - 1,
            });
          }
          return updated;
        });
      }, 1000);

      countdownIntervalsRef.current.set(fileId, countdownInterval);

      // Set timeout to call backend delete after 5 seconds
      const deleteTimeout = setTimeout(async () => {
        // Clear the countdown interval
        const interval = countdownIntervalsRef.current.get(fileId);
        if (interval) {
          clearInterval(interval);
          countdownIntervalsRef.current.delete(fileId);
        }

        // Update file status to "deleting" to show loading indicator
        setUploadStates((prev) => {
          const updated = new Map(prev);
          updated.set(fileId, {
            ...fileToDelete,
            status: FileStatus.DELETING,
            deleteTimer: undefined,
          });
          return updated;
        });

        try {
          // Call backend delete endpoint (soft delete)
          await deleteDocumentMutation.mutateAsync({
            assessmentId,
            assessmentDomainId,
            documentId: fileToDelete.id,
          });

          setDeletedFiles((prev) => new Set(prev).add(fileId));

          // Mark file as deleted in uploadingFiles
          setUploadStates((prev) => {
            const updated = new Map(prev);
            updated.set(fileId, { ...fileToDelete, status: FileStatus.DELETED, deleteTimer: undefined });
            return updated;
          });

          const queryKey = [
            'assessments',
            assessmentId,
            'domains',
          ];
          queryClient.invalidateQueries({
            queryKey: queryKey,
            // refetchType: 'all',
          });

          enqueueSnackbar(t('app.assessment.workflow.documentDeleted', { fileName: fileToDelete.fileName }), {
            variant: 'success',
          });
        } catch (error) {
          console.error('Failed to delete file:', error);
          const errorMessage = getLocalizedErrorMessage(error, t, 'Failed to delete file. Please try again.');

          // Mark as delete failed
          setUploadStates((prev) => {
            const updated = new Map(prev);
            updated.set(fileId, {
              ...fileToDelete,
              status: FileStatus.DELETE_FAILED,
              deleteTimer: undefined,
              error: errorMessage,
            });
            return updated;
          });

          enqueueSnackbar(errorMessage, {
            variant: 'error',
          });
        }

        // Clean up timer reference
        deleteTimersRef.current.delete(fileId);
      }, 5000);

      // Store the timeout ID so we can cancel it if user clicks undo
      deleteTimersRef.current.set(fileId, deleteTimeout);
    },
    [
      assessmentId,
      assessmentDomainId,
      deleteDocumentMutation,
      domainDocuments,
      enqueueSnackbar,
      uploadStates,
      domainStatus,
    ],
  );

  /**
   * Undoes a file deletion
   * - If file is in "pendingDelete" status: Cancel the timer and restore immediately
   * - If file is already "deleted" in backend: Refetch to restore (backend still has it soft-deleted)
   */
  const handleUndoDelete = useCallback(
    (fileId: string) => {
      const fileData = deletedFilesData.get(fileId);
      if (!fileData) return;

      const currentFile = uploadStates.get(fileId);

      // Cancel pending delete timer if it exists
      const deleteTimeout = deleteTimersRef.current.get(fileId);
      if (deleteTimeout) {
        clearTimeout(deleteTimeout);
        deleteTimersRef.current.delete(fileId);
      }

      // Cancel countdown interval if it exists
      const countdownInterval = countdownIntervalsRef.current.get(fileId);
      if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownIntervalsRef.current.delete(fileId);
      }

      // If file was pending delete, restore it immediately
      if (currentFile?.status === FileStatus.PENDING_DELETE) {
        setUploadStates((prev) => {
          const updated = new Map(prev);
          updated.set(fileId, {
            ...fileData,
            status: FileStatus.UPLOADED,
            deleteTimer: undefined,
          });
          return updated;
        });

        setDeletedFilesData((prev) => {
          const updated = new Map(prev);
          updated.delete(fileId);
          return updated;
        });


        return;
      }

      // File was already deleted in backend, need to refetch
      setDeletedFiles((prev) => {
        const updated = new Set(prev);
        updated.delete(fileId);
        return updated;
      });

      // Remove from uploadingFiles since file is back in backend data
      setUploadStates((prev) => {
        const updated = new Map(prev);
        updated.delete(fileId);
        return updated;
      });

      setDeletedFilesData((prev) => {
        const updated = new Map(prev);
        updated.delete(fileId);
        return updated;
      });

      enqueueSnackbar(t('app.assessment.workflow.fileRestored', { fileName: fileData.fileName }), {
        variant: 'info',
      });

      const queryKey = [
        'assessments',
        assessmentId,
        'domains',
        // assessmentDomainId,
        // { assessmentId, assessmentDomainIdentifier: assessmentDomainId },
        // { includeAssessment: true },
      ];

      // Refetch domain details to show the restored file
      queryClient.invalidateQueries({
        queryKey: queryKey,
        // refetchType: 'all',
      });
    },
    [deletedFilesData, uploadStates, enqueueSnackbar, queryClient, assessmentId, assessmentDomainId, t],
  );

  /**
   * Retries a failed deletion
   * Attempts to delete the file from the backend
   */
  const handleRetryDelete = useCallback(
    async (fileId: string) => {
      if (!assessmentId || !assessmentDomainId) return;

      // Find document by id (for UI matching), but use documentId for API call
      const document = domainDocuments?.find((doc) => doc.id === fileId);
      if (!document) {
        enqueueSnackbar(t('app.assessment.workflow.documentNotFound'), {
          variant: 'error',
        });
        return;
      }

      // Use documentId for the delete API call (not id)
      const documentIdToDelete = document.documentId;
      if (!documentIdToDelete) {
        enqueueSnackbar(t('app.assessment.workflow.documentIdNotFound'), {
          variant: 'error',
        });
        setDeletedFiles((prev) => {
          const updated = new Set(prev);
          updated.delete(fileId);
          return updated;
        });
        return;
      }

      const uploadingFile = uploadStates.get(fileId);
      if (uploadingFile) {
        setUploadStates((prev) => {
          const updated = new Map(prev);
          updated.set(fileId, { ...uploadingFile, status: FileStatus.DELETING }); // Show loading indicator
          return updated;
        });
      }

      try {
        await deleteDocumentMutation.mutateAsync({
          assessmentId,
          assessmentDomainId,
          documentId: documentIdToDelete,
        });

        // On success, remove from deletedFiles and mark as actually deleted
        setDeletedFiles((prev) => {
          const updated = new Set(prev);
          updated.delete(fileId);
          return updated;
        });

        setUploadStates((prev) => {
          const updated = new Map(prev);
          updated.delete(fileId);
          return updated;
        });

        const queryKey = [
          'assessments',
          assessmentId,
          'domains',
          // assessmentDomainId,
          // { assessmentId, assessmentDomainIdentifier: assessmentDomainId },
          // { includeAssessment: true },
        ];
        queryClient.invalidateQueries({
          queryKey: queryKey,
          // refetchType: 'all',
        });

        enqueueSnackbar(t('app.assessment.workflow.documentDeleted', { fileName: document.fileName }), {
          variant: 'success',
        });
      } catch (error) {
        console.error(t('app.assessment.workflow.failedToDeleteDocument'), error);

        // Mark as deleteFailed
        const uploadingFile = uploadStates.get(fileId);
        if (uploadingFile) {
          setUploadStates((prev) => {
            const updated = new Map(prev);
            updated.set(fileId, { ...uploadingFile, status: FileStatus.DELETE_FAILED, error: getLocalizedErrorMessage(error, t, t('app.assessment.workflow.deletionFailed')) });
            return updated;
          });
        }

        const errorMessage = getLocalizedErrorMessage(error, t, t('app.assessment.workflow.failedToDeleteDocument'));
        enqueueSnackbar(errorMessage, {
          variant: 'error',
        });
      }
    },
    [
      assessmentId,
      assessmentDomainId,
      domainDocuments,
      deleteDocumentMutation,
      queryClient,
      enqueueSnackbar,
      uploadStates,
    ],
  );

  /**
   * Hide a document that was just moved to another domain. Removes any local
   * upload state for it and flags it so `filesArray` drops it immediately,
   * without waiting for the background domain refetch.
   */
  const markFileAsMoved = useCallback((documentId: string) => {
    setMovedFiles((prev) => new Set(prev).add(documentId));
    setUploadStates((prev) => {
      if (!prev.has(documentId)) return prev;
      const updated = new Map(prev);
      updated.delete(documentId);
      return updated;
    });
  }, []);

  /**
   * Clears all deleted files (called on successful save)
   */
  const clearDeletedFiles = useCallback(() => {
    setDeletedFiles(new Set());
    setDeletedFilesData(new Map());
  }, []);

  /**
   * Restores all deleted files (called on cancel/discard)
   */
  const restoreDeletedFiles = useCallback(() => {
    deletedFilesData.forEach((fileData, fileId) => {
      if (fileData.status === FileStatus.UPLOADED) {
        setUploadStates((prev) => {
          const updated = new Map(prev);
          updated.set(fileId, { ...fileData, status: FileStatus.UPLOADED });
          return updated;
        });
      }
    });
    setDeletedFiles(new Set());
    setDeletedFilesData(new Map());
  }, [deletedFilesData]);

  /**
   * Retries a failed upload
   * - Reuses the same uploadId and uploadedAt so the file stays in-place in the list
   * - Skips all validation (count, domain status) since the file is already tracked
   * - Delegates directly to uploadFileEntries
   */
  const handleRetryUpload = useCallback(
    async (fileId: string) => {
      const originalFile = originalFilesRef.current.get(fileId);
      if (!originalFile) {
        enqueueSnackbar(t('app.assessment.workflow.retryUploadFailed'), {
          variant: 'error',
        });
        return;
      }

      // Preserve the original uploadedAt so the file stays in its current position
      const existingState = uploadStates.get(fileId);
      const uploadedAt = existingState?.uploadedAt ?? new Date().toISOString();

      await uploadFileEntries([{ file: originalFile, uploadId: fileId, uploadedAt }]);
    },
    [uploadFileEntries, uploadStates, enqueueSnackbar, t],
  );

  /**
   * Removes a failed upload from the UI
   * - Cleans up the failed file entry and its stored original File object
   */
  const handleRemoveFailedUpload = useCallback(
    (fileId: string) => {
      setUploadStates((prev) => {
        const updated = new Map(prev);
        updated.delete(fileId);
        return updated;
      });

      originalFilesRef.current.delete(fileId);
    },
    [],
  );

  /**
   * Cancels an in-progress upload
   * - Aborts the S3 upload request
   * - Removes file from UI (both uploadStates and uploadingFiles)
   * - Cleans up upload state
   * 
   * This is called when user clicks cancel on a file that's currently uploading
   */
  const handleCancelUpload = useCallback(
    (uploadId: string) => {
      const uploadState = uploadStates.get(uploadId);

      // Abort the active XHR/fetch if the file is currently uploading
      if (uploadState?.id) {
        cancelUpload(uploadState.id);
      }

      // Remove from originalFilesRef so the drain loop skips it if still queued
      originalFilesRef.current.delete(uploadId);

      // Remove from the pending queue if not yet started
      uploadQueueRef.current = uploadQueueRef.current.filter((e) => e.uploadId !== uploadId);

      if (uploadState) {
        setUploadStates((prev) => {
          const updated = new Map(prev);
          updated.delete(uploadId);
          return updated;
        });

        enqueueSnackbar(t('app.assessment.workflow.uploadCancelled', { fileName: uploadState.fileName }), {
          variant: 'info',
        });
      }
    },
    [uploadStates, cancelUpload, enqueueSnackbar],
  );

  return {
    filesArray,
    deletedFiles,
    handleAddFiles,
    handleDeleteFile,
    handleUndoDelete,
    handleRetryDelete,
    handleRetryUpload,
    handleRemoveFailedUpload,
    handleCancelUpload,
    markFileAsMoved,
    clearDeletedFiles,
    restoreDeletedFiles,
  };
};